import React, { useEffect, useRef } from 'react'
import MapView from './MapView'
import MapControls from './controls/MapControls'
import { CoffeeShop } from '../../hooks/useCoffeeShops'
import { ViewState, LayerConfig, VisualizationType } from '../../types/map'

interface MapContainerProps {
  viewState: ViewState
  setViewState: (v: ViewState | ((prev: ViewState) => ViewState)) => void
  coffeeShops: CoffeeShop[]
  layers: LayerConfig[]
  setLayers: React.Dispatch<React.SetStateAction<LayerConfig[]>>
  visualizationType?: VisualizationType
  heatmapIntensity?: number
  onDragStateChange?: (isDragging: boolean) => void
  onVisualizationTypeChange?: (type: VisualizationType) => void
}

const MapContainer: React.FC<MapContainerProps> = ({ viewState, setViewState, coffeeShops, layers, setLayers, visualizationType, heatmapIntensity, onDragStateChange, onVisualizationTypeChange }) => {
  const hasAutoZoomed = useRef(false)

  // Zoom controls
  const setZoom = (z: number) => {
    setViewState((vs: ViewState) => ({ ...vs, zoom: Math.max(2, Math.min(19, z)) }))
  }

  // Visualization type change handler
  const handleVisualizationTypeChange = (type: VisualizationType) => {
    onVisualizationTypeChange?.(type)
  }

  // Auto-zoom to fit all coffee shops only on first load
  useEffect(() => {
    if (coffeeShops.length > 0 && !hasAutoZoomed.current) {
      hasAutoZoomed.current = true
      const lats = coffeeShops.map(s => s.lat)
      const lons = coffeeShops.map(s => s.lon)
      const minLat = Math.min(...lats)
      const maxLat = Math.max(...lats)
      const minLon = Math.min(...lons)
      const maxLon = Math.max(...lons)
      // Center
      const centerLat = (minLat + maxLat) / 2
      const centerLon = (minLon + maxLon) / 2
      // Estimate zoom (very rough, for demo)
      const latDiff = maxLat - minLat
      const lonDiff = maxLon - minLon
      let zoom = 12
      if (latDiff > 0.2 || lonDiff > 0.2) zoom = 10
      if (latDiff > 0.5 || lonDiff > 0.5) zoom = 8
      if (latDiff > 1.0 || lonDiff > 1.0) zoom = 6
      setViewState((vs: ViewState) => ({ ...vs, latitude: centerLat, longitude: centerLon, zoom }))
    }
  }, [coffeeShops, setViewState])

  return (
    <div className="map-container">
      {/* Map View */}
      <MapView
        viewState={viewState}
        setViewState={setViewState}
        coffeeShops={coffeeShops}
        layers={layers}
        visualizationType={visualizationType || 'scatter'}
        heatmapIntensity={heatmapIntensity || 2}
        onDragStateChange={onDragStateChange || (() => {})}
      />
      
      {/* Map Controls */}
      <div className="absolute top-4 right-4 z-10">
        <MapControls 
          zoom={viewState.zoom} 
          setZoom={setZoom}
          layers={layers}
          setLayers={setLayers}
          visualizationType={visualizationType || 'scatter'}
          onVisualizationTypeChange={handleVisualizationTypeChange}
        />
      </div>
    </div>
  )
}

export default MapContainer 